"use client";

import { Box, Divider, Flex, useDisclosure } from "@chakra-ui/react";
import { Suspense, useCallback, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import { MicrositeListingHero } from "@/components/shared/MicrositeListingHero";
import { PublicEntrance } from "@/components/shared/motion";
import { CustomSelect } from "@/components/shared/formElements/CustomSelect";
import { FiltersTriggerButton } from "@/components/ui";
import { usePublicListingPager, useTeachersGetPublic, useTenantData } from "@/hooks";
import { useTenantContentSettingsGet } from "@/features/profile-settings/hooks/useTenantContentSettingsGet";
import { useThemeTokens } from "@/lib/theme/tokens";
import { usePrefetchPublicListingMediaUrls } from "@/features/media/hooks";
import { persistPublicListingHref } from "@/lib/utils/publicListingReturn";
import { getTenantDisplayName } from "@/lib/utils/tenant";
import { getActivePublicTeachers } from "@/lib/utils/teacherPublic";
import { EntryType, MediaGroup } from "@/openapi/generated/models";
import {
  buildTeachersPageQuery,
  EMPTY_TEACHERS_PAGE_URL_STATE,
  teachersPageParamsMatchSearchParams,
  TEACHERS_PAGE_SIZE,
  type TeachersPageUrlState,
} from "./hooks/teachersPageUrl";
import { useTeachersFilter } from "./hooks/useTeachersFilter";
import { TeachersList } from "./TeachersList";
import {
  TeachersPageEmpty,
  TeachersPageError,
  TeachersPageNoResults,
} from "./TeachersPageError";
import { TeachersPageSkeleton } from "./TeachersPageSkeleton";
import {
  countTeachersPublicFilters,
  EMPTY_TEACHERS_PUBLIC_FILTERS_DRAFT,
  TeachersPublicFiltersModal,
  type TeachersPublicFiltersDraft,
} from "./TeachersPublicFiltersModal";
import {
  PublicFilterChip,
  PublicFilterChipGroup,
  PublicFilterClearAll,
  PublicListingCount,
  PublicListingPagination,
  PublicListingResultsMeta,
  PublicListingSearchField,
  PublicListingToolbar,
  PublicListingUrlSync,
} from "@/views/shared/public-listing";

export const TeachersPageClient = ({
  hideHero = false,
  initialUrlState = EMPTY_TEACHERS_PAGE_URL_STATE,
}: {
  hideHero?: boolean;
  initialUrlState?: TeachersPageUrlState;
}) => {
  const theme = useThemeTokens();
  const pathname = usePathname();
  const { tenantId, tenantData } = useTenantData();
  const studioName = getTenantDisplayName(tenantData);
  const { data: contentSettings } = useTenantContentSettingsGet(
    { tenantId },
    { enabled: !!tenantId },
  );

  const {
    data,
    isPending,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useTeachersGetPublic({ page: 1, pageSize: 1000, active: true });

  const teachers = useMemo(
    () => getActivePublicTeachers(data?.items),
    [data?.items],
  );

  const {
    search,
    setSearch,
    locationFilter,
    setLocationFilter,
    filteredData,
    hasActiveFilters,
    resetFilters,
    isSearchPending,
  } = useTeachersFilter(teachers, {
    search: initialUrlState.search,
    locationFilter: initialUrlState.location,
  });

  const {
    isOpen: isFiltersModalOpen,
    onOpen: onFiltersModalOpen,
    onClose: onFiltersModalClose,
  } = useDisclosure();

  const {
    currentPage,
    totalPages,
    paginatedData,
    listTopRef,
    rangeStart,
    rangeEnd,
    resetToFirstPage,
    handlePrevious,
    handleNext,
  } = usePublicListingPager({
    items: filteredData,
    pageSize: TEACHERS_PAGE_SIZE,
    initialPageZeroBased: Math.max(0, initialUrlState.page - 1),
    enabled: !isFiltersModalOpen,
  });

  const urlState = useMemo<TeachersPageUrlState>(
    () => ({
      search,
      location: locationFilter,
      page: currentPage + 1,
    }),
    [search, locationFilter, currentPage],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      resetToFirstPage();
    },
    [setSearch, resetToFirstPage],
  );

  const handleLocationChange = useCallback(
    (value: string | null) => {
      setLocationFilter(value);
      resetToFirstPage();
    },
    [setLocationFilter, resetToFirstPage],
  );

  const handleResetFilters = useCallback(() => {
    resetFilters();
    resetToFirstPage();
  }, [resetFilters, resetToFirstPage]);

  const [filtersDraft, setFiltersDraft] = useState<TeachersPublicFiltersDraft>(
    EMPTY_TEACHERS_PUBLIC_FILTERS_DRAFT,
  );

  const appliedFiltersDraft = useMemo<TeachersPublicFiltersDraft>(
    () => ({ location: locationFilter }),
    [locationFilter],
  );

  const activeFilterCount = countTeachersPublicFilters(appliedFiltersDraft);
  const hasUnappliedFilters =
    isFiltersModalOpen && filtersDraft.location !== appliedFiltersDraft.location;

  const openFiltersModal = useCallback(() => {
    setFiltersDraft(appliedFiltersDraft);
    onFiltersModalOpen();
  }, [appliedFiltersDraft, onFiltersModalOpen]);

  const handleApplyFiltersDraft = useCallback(() => {
    setLocationFilter(filtersDraft.location);
    resetToFirstPage();
  }, [filtersDraft.location, setLocationFilter, resetToFirstPage]);

  const handleClearFiltersDraft = useCallback(() => {
    setFiltersDraft(EMPTY_TEACHERS_PUBLIC_FILTERS_DRAFT);
  }, []);

  const persistListingHref = useCallback((href: string) => {
    persistPublicListingHref("teachers", href);
  }, []);

  const listingHref = `${pathname}${buildTeachersPageQuery(urlState)}`;
  const isCurrentSearch = useCallback(
    (searchParams: URLSearchParams) =>
      teachersPageParamsMatchSearchParams(urlState, searchParams),
    [urlState],
  );

  const locationOptions = useMemo(() => {
    const names = teachers.flatMap((teacher) =>
      (teacher.workLocations ?? [])
        .map((loc) => loc.name?.trim())
        .filter((name): name is string => Boolean(name)),
    );
    return Array.from(new Set(names)).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }, [teachers]);

  const showLocationFilter = locationOptions.length > 1;

  const nextPageOwnerIds = useMemo(
    () =>
      filteredData
        .slice(rangeEnd, rangeEnd + TEACHERS_PAGE_SIZE)
        .map((teacher) => Number(teacher.teacherId))
        .filter((id) => id > 0),
    [filteredData, rangeEnd],
  );

  usePrefetchPublicListingMediaUrls({
    ownerType: EntryType.Teacher,
    ownerIds: nextPageOwnerIds,
    mediaGroup: MediaGroup.Profile,
  });

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  if ((isLoading || isPending) && !data) {
    return <TeachersPageSkeleton hideHero={hideHero} />;
  }

  if (isError) {
    return <TeachersPageError onRetry={handleRetry} isRetrying={isFetching} />;
  }

  if (teachers.length === 0) {
    return <TeachersPageEmpty />;
  }

  const tenantEmail = tenantData?.email ?? null;
  const hasSearchOrFilters = hasActiveFilters || search.trim().length > 0;

  const renderSearch = (describedById: string) => (
    <Box
      w={{ base: "100%", md: "auto" }}
      minW={{ base: 0, md: "320px" }}
      maxW={{ md: "420px" }}
      flexShrink={{ md: 0 }}
      overflow="hidden"
      ml={showLocationFilter ? undefined : { md: "auto" }}
    >
      <PublicListingSearchField
        value={search}
        onChange={handleSearchChange}
        placeholder="Search by first or last name..."
        describedById={describedById}
        description={`Search through ${teachers.length} instructors`}
        isPending={isSearchPending}
      />
    </Box>
  );

  const filterChips = hasActiveFilters ? (
    <PublicFilterChipGroup>
      {locationFilter ? (
        <PublicFilterChip
          label={`Location: ${locationFilter}`}
          removeLabel={`Remove location filter ${locationFilter}`}
          onRemove={() => handleLocationChange(null)}
        />
      ) : null}
      <PublicFilterClearAll onClear={handleResetFilters} />
    </PublicFilterChipGroup>
  ) : null;

  return (
    <Flex
      direction="column"
      gap={{ base: 5, md: 6 }}
      maxW="1044px"
      mx="auto"
      w="100%"
    >
      <Suspense fallback={null}>
        <PublicListingUrlSync
          href={listingHref}
          persist={persistListingHref}
          isCurrentSearch={isCurrentSearch}
        />
      </Suspense>
      {!hideHero ? (
        <MicrositeListingHero
          subtitle={contentSettings.teachersSubtitle}
          title={contentSettings.teachersTitle}
          description={contentSettings.teachersDescription}
          studioName={studioName}
          tenantEmail={tenantEmail}
        />
      ) : null}

      {hideHero ? (
        <Divider borderColor={theme.borderGlass.subtle} opacity={0.7} />
      ) : null}

      <PublicEntrance variant="group" delay={0.1}>
        <PublicListingToolbar>
          {showLocationFilter ? (
            <Flex
              display={{ base: "flex", md: "none" }}
              gap="10px"
              w="100%"
              minW={0}
              overflow="hidden"
              align="center"
            >
              <FiltersTriggerButton
                onClick={openFiltersModal}
                activeCount={activeFilterCount}
                hasUnapplied={hasUnappliedFilters}
                w="auto"
                flexShrink={0}
                data-testid="teachers-public-filters-trigger"
              />
              <Box flex={1} minW={0} overflow="hidden">
                {renderSearch("teachers-search-description-mobile")}
              </Box>
            </Flex>
          ) : (
            <Box display={{ base: "block", md: "none" }} w="100%" minW={0} overflow="hidden">
              {renderSearch("teachers-search-description-mobile")}
            </Box>
          )}

          {showLocationFilter ? (
            <Box
              display={{ base: "none", md: "block" }}
              flex={1}
              minW={{ md: "220px" }}
            >
              <CustomSelect
                placeholder="All Locations"
                formControlProps={{
                  w: { base: "100%", md: "max-content" },
                }}
                options={[
                  { label: "All Locations", value: "" },
                  ...locationOptions.map((location) => ({
                    label: location,
                    value: location,
                  })),
                ]}
                value={locationFilter ?? ""}
                onChange={(val) =>
                  handleLocationChange(val === "" ? null : val)
                }
              />
            </Box>
          ) : null}

          <Box display={{ base: "none", md: "block" }}>
            {renderSearch("teachers-search-description")}
          </Box>
        </PublicListingToolbar>

        {filteredData.length === 0 ? (
          <>
            <PublicListingResultsMeta chips={filterChips} />
            <TeachersPageNoResults hasActiveQuery={hasSearchOrFilters} />
          </>
        ) : (
          <>
            <PublicListingResultsMeta chips={filterChips}>
              <PublicListingCount
                count={filteredData.length}
                singular="instructor"
                plural="instructors"
              />
            </PublicListingResultsMeta>
            <Box ref={listTopRef} position="relative">
              <TeachersList data={paginatedData} />
            </Box>
            <PublicListingPagination
              currentPageZeroBased={currentPage}
              totalPages={totalPages}
              totalCount={filteredData.length}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              onPrev={handlePrevious}
              onNext={handleNext}
              ariaLabel="Instructor list pagination"
            />
          </>
        )}
      </PublicEntrance>

      {showLocationFilter ? (
        <TeachersPublicFiltersModal
          isOpen={isFiltersModalOpen}
          onClose={onFiltersModalClose}
          draft={filtersDraft}
          onDraftChange={setFiltersDraft}
          onApply={handleApplyFiltersDraft}
          onClearDraft={handleClearFiltersDraft}
          locationOptions={locationOptions}
        />
      ) : null}
    </Flex>
  );
};
